import { jsPDF } from 'jspdf';
import { calculateVat } from './tax';

const ACCENT = '#7c6fff';
const ACCENT_2 = '#22d3c5';
const INK = '#14151c';
const GRAY = '#6b6e85';
const LIGHT_GRAY = '#e2e2ec';

const STATUS_COLORS = {
  paid: '#0e9a8f',
  pending: '#b8790a',
  failed: '#d6455e',
};

function formatKES(amount) {
  return `KES ${Number(amount || 0).toLocaleString()}`;
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * Builds a one-page invoice PDF from an invoice + user record and triggers a
 * browser download. Entirely client-side — no server round trip.
 *
 * `options.description` and `options.invoiceNumber` let callers without a
 * real invoice record (e.g. a plan-upgrade checkout) render a sensible
 * document instead of the default "<package> subscription" / "INV-000001".
 */
export function downloadInvoicePdf(invoice, user, options = {}) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 48;
  const contentRight = pageWidth - margin;
  const invoiceNumber = options.invoiceNumber || `INV-${String(invoice.id).padStart(6, '0')}`;

  // Header: brand mark + name, invoice number on the right
  doc.setFillColor(ACCENT);
  doc.roundedRect(margin, 40, 28, 28, 6, 6, 'F');
  doc.setTextColor('#ffffff');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('P', margin + 14, 59, { align: 'center' });

  doc.setTextColor(INK);
  doc.setFontSize(15);
  doc.text('Piua AI', margin + 38, 59);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text('INVOICE', contentRight, 54, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(GRAY);
  doc.text(invoiceNumber, contentRight, 70, { align: 'right' });

  doc.setDrawColor(LIGHT_GRAY);
  doc.line(margin, 96, contentRight, 96);

  // Billed to / invoice details — for corporate accounts the invoice is
  // addressed to the entity (its name and email), with the submitting
  // user named only as the "Attn:" contact, never as the billed party.
  const billedToName = user?.entity ? user.entity.name : user?.fullname;
  const billedToSub = user?.entity ? `Attn: ${user.fullname}` : user?.email;
  const billedToEmail = user?.entity ? user.entity.email : null;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(GRAY);
  doc.text('BILLED TO', margin, 126);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(INK);
  doc.text(billedToName || '—', margin, 144);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(GRAY);
  doc.text(billedToSub || '', margin, 160);
  if (billedToEmail) doc.text(billedToEmail, margin, 174);

  const detailX = contentRight;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(GRAY);
  doc.text('INVOICE DETAILS', detailX, 126, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(INK);
  doc.text(`Issued: ${formatDate(invoice.createdAt)}`, detailX, 144, { align: 'right' });
  doc.text(
    `Billing period: ${formatDate(invoice.periodStart)} – ${formatDate(invoice.periodEnd)}`,
    detailX,
    160,
    { align: 'right' }
  );

  doc.setFont('helvetica', 'bold');
  doc.setTextColor(STATUS_COLORS[invoice.status] || GRAY);
  doc.text(`Status: ${(invoice.status || '').toUpperCase()}`, detailX, 176, { align: 'right' });

  // Line-item table
  const tableTop = 220;
  const col1 = margin;
  const col2 = margin + 300;
  const col3 = contentRight;

  doc.setFillColor('#f4f4f9');
  doc.rect(margin, tableTop, contentRight - margin, 26, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(GRAY);
  doc.text('DESCRIPTION', col1 + 8, tableTop + 17);
  doc.text('PERIOD', col2, tableTop + 17);
  doc.text('AMOUNT', col3 - 8, tableTop + 17, { align: 'right' });

  const rowTop = tableTop + 26;
  const rowHeight = 34;
  doc.setDrawColor(LIGHT_GRAY);
  doc.line(margin, rowTop + rowHeight, contentRight, rowTop + rowHeight);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10.5);
  doc.setTextColor(INK);
  doc.text(options.description || `${user?.packageName || 'Piua AI'} subscription`, col1 + 8, rowTop + 21);
  doc.setFontSize(9.5);
  doc.setTextColor(GRAY);
  doc.text(`${formatDate(invoice.periodStart)} – ${formatDate(invoice.periodEnd)}`, col2, rowTop + 21);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(INK);
  doc.text(formatKES(invoice.amount), col3 - 8, rowTop + 21, { align: 'right' });

  // Totals: subtotal, VAT, then total due
  const vat = calculateVat(invoice.amount);
  const total = invoice.amount + vat;

  let totalsY = rowTop + rowHeight + 24;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10.5);
  doc.setTextColor(GRAY);
  doc.text('Subtotal', col3 - 140, totalsY, { align: 'right' });
  doc.setTextColor(INK);
  doc.text(formatKES(invoice.amount), col3 - 8, totalsY, { align: 'right' });

  totalsY += 18;
  doc.setTextColor(GRAY);
  doc.text('VAT (16%)', col3 - 140, totalsY, { align: 'right' });
  doc.setTextColor(INK);
  doc.text(formatKES(vat), col3 - 8, totalsY, { align: 'right' });

  totalsY += 14;
  doc.setDrawColor(LIGHT_GRAY);
  doc.line(col3 - 180, totalsY, col3, totalsY);

  totalsY += 24;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(GRAY);
  doc.text('Total Due', col3 - 140, totalsY, { align: 'right' });
  doc.setFontSize(16);
  doc.setTextColor(ACCENT_2);
  doc.text(formatKES(total), col3 - 8, totalsY, { align: 'right' });

  // Footer
  const footerY = 780;
  doc.setDrawColor(LIGHT_GRAY);
  doc.line(margin, footerY - 24, contentRight, footerY - 24);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(GRAY);
  doc.text('Thank you for choosing Piua AI.', pageWidth / 2, footerY, { align: 'center' });
  doc.setFontSize(8);
  doc.text(
    'This is a computer-generated invoice and does not require a signature.',
    pageWidth / 2,
    footerY + 14,
    { align: 'center' }
  );

  doc.save(`piua-${invoiceNumber}.pdf`);
}
